import { fetchAuthSession } from "aws-amplify/auth";
import axios from "axios";
import { errorDeApi } from "./client";


const getTokens = async () => {
    // Sin forceRefresh: ver el comentario en libs/axios.js.
    const { tokens } = await fetchAuthSession().catch(() => ({}));
    return tokens;
};


// Helper interno para obtener headers de auth
const getAuthHeaders = async () => {
    const tokens = await getTokens();
    const accessToken = tokens?.accessToken?.toString();
    // ⚠️ Sin el guard, cuando no había token esto mandaba el string literal
    // "Bearer undefined": el backend lo daba por presente (validateTokenExist
    // solo mira que venga algo) y después reventaba en cognito-express con
    // "Not a valid JWT token" → 401 "El token de acceso no es válido".
    // Mejor no mandar el header y que el 401 sea inequívoco.
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};


// Helper interno para descargar un blob como archivo
const descargarBlob = async (url, filename, mimeType = 'text/csv;charset=utf-8;') => {
    const headers = await getAuthHeaders();
    const response = await axios.get(url, {
        headers,
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: mimeType });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Intentar obtener filename desde el header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (match?.[1]) filename = match[1].replace(/['"]/g, '');
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
};


const ROL_ROTATION = ["coordinador", "jefe_recinto", "ventas", "reporte", "admin"];


const Post = async (payload) => {
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_API_USERS}/api/admin/users`,
            payload,
            { headers: await getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const getAllUsers = async ({ page = 1, limit = 20, role, isActive, search } = {}) => {
    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_USERS}/api/admin/users`,
            {
                headers: await getAuthHeaders(),
                params: { page, limit, role, isActive, search }
            }
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


/**
 * Exportar todos los usuarios a CSV
 * @param {Object} filtros - { role, isActive, search } — mismos filtros que la lista
 */
const exportarUsuariosCSV = async (filtros = {}) => {
    try {
        const params = new URLSearchParams();
        if (filtros.role)                          params.append('role',     filtros.role);
        if (filtros.isActive !== undefined)        params.append('isActive', filtros.isActive);
        if (filtros.search)                        params.append('search',   filtros.search);

        const queryString = params.toString();
        const url = `${process.env.REACT_APP_API_USERS}/api/admin/users/export/csv${queryString ? `?${queryString}` : ''}`;
        const filename = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;

        await descargarBlob(url, filename);

        return { success: true, message: 'CSV descargado exitosamente' };
    } catch (error) {
        console.error('Error exportando CSV usuarios:', error);
        throw error.response?.data || error;
    }
}


/**
 * Parser CSV mínimo (RFC 4180): respeta comillas, comillas dobles escapadas y
 * saltos de línea dentro de una celda, que es justo lo que trae una dirección.
 */
const parsearCSV = (texto) => {
    const filas = [];
    let fila = [];
    let celda = '';
    let enComillas = false;
    const s = texto.replace(/^﻿/, '');
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (enComillas) {
            if (c === '"' && s[i + 1] === '"') { celda += '"'; i++; }
            else if (c === '"') enComillas = false;
            else celda += c;
        } else if (c === '"') enComillas = true;
        else if (c === ',') { fila.push(celda); celda = ''; }
        else if (c === '\n' || c === '\r') {
            if (c === '\r' && s[i + 1] === '\n') i++;
            fila.push(celda); filas.push(fila); fila = []; celda = '';
        } else celda += c;
    }
    if (celda !== '' || fila.length) { fila.push(celda); filas.push(fila); }
    return filas.filter(f => f.some(v => v !== ''));
};

/**
 * Trae los usuarios a exportar como objetos { 'Etiqueta columna': valor }.
 * Reusa el endpoint CSV (liviano, aguanta el techo de payload del Lambda) y el
 * Excel con formato se arma en el navegador con utils/exportExcel.js.
 * @param {Object} filtros - { role, isActive, search } — mismos filtros que la lista
 */
const obtenerUsuariosExport = async (filtros = {}) => {
    try {
        const params = new URLSearchParams();
        if (filtros.role)                          params.append('role',     filtros.role);
        if (filtros.isActive !== undefined)        params.append('isActive', filtros.isActive);
        if (filtros.search)                        params.append('search',   filtros.search);

        const queryString = params.toString();
        const url = `${process.env.REACT_APP_API_USERS}/api/admin/users/export/csv${queryString ? `?${queryString}` : ''}`;
        const headers = await getAuthHeaders();
        // Sin responseType: así un error JSON (404 sin usuarios) llega como objeto y
        // errorDeApi saca su mensaje; el CSV llega igual como string.
        const response = await axios.get(url, { headers });

        const [encabezado = [], ...datos] = parsearCSV(response.data || '');
        return datos.map(f => Object.fromEntries(encabezado.map((h, i) => [h, f[i] ?? ''])));
    } catch (error) {
        console.error('Error obteniendo usuarios para exportar:', error);
        throw errorDeApi(error);
    }
}


const updateUser = async (userId, payload) => {
    try {
        const response = await axios.patch(
            `${process.env.REACT_APP_API_USERS}/api/admin/users/${userId}`,
            payload,
            { headers: await getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const updateUserRole = async (userId, payload) => {
    try {
        const response = await axios.patch(
            `${process.env.REACT_APP_API_USERS}/api/admin/users/${userId}/role`,
            payload,
            { headers: await getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const nextRolSugerido = async (currentRole) => {
    const idx = ROL_ROTATION.indexOf((currentRole || "").toLowerCase());
    if (idx === -1) return ROL_ROTATION[0];
    return ROL_ROTATION[(idx + 1) % ROL_ROTATION.length];
}


const toggleUserStatus = async (userId, enable) => {
    try {
        const response = await axios.patch(
            `${process.env.REACT_APP_API_USERS}/api/admin/users/${userId}/status`,
            { enable },
            { headers: await getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const Delete = async (userId) => {
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_API_USERS}/api/admin/users/delete/${userId}`,
            { hardDelete: true },
            { headers: await getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


/**
 * Busca un usuario específico por email o RUT
 * @param {string} query - Email o RUT del usuario
 */
const buscarPorEmailORut = async (query) => {
    try {
        if (!query?.trim()) throw new Error('Debe proporcionar un email o RUT');

        const response = await getAllUsers({ search: query.trim(), limit: 10, page: 1 });
        const users = response.data?.users || [];

        // Solo coincidencia EXACTA de email o RUT. Antes, si no había una, devolvía el
        // primer resultado de la búsqueda: al buscar "juan" salía "Usuario encontrado:
        // Juan X" y se le asignaba la matrícula (o el taller) a otra persona.
        // El RUT se compara sin puntos ni guion: "12.345.678-9" = "12345678-9".
        const limpiarRut = (r) => (r || '').replace(/[.\-\s]/g, '').toLowerCase();
        const q = query.toLowerCase().trim();
        const target = users.find(user =>
            user.email?.toLowerCase() === q || (user.rut && limpiarRut(user.rut) === limpiarRut(q)));
        if (!target) return null;

        return {
            cognitoId: target.cognitoData?.attributes?.sub || target.userId,
            email:     target.email,
            rut:       target.rut,
            nombre:    target.name || target.nombre || target.email.split('@')[0],
            telefono:  target.phone || target.cognitoData?.attributes?.phone_number || '',
        };
    } catch (error) {
        console.error('Error buscando usuario:', error);
        throw error;
    }
};


/**
 * Busca usuarios que coincidan con el query (para autocomplete)
 * @param {string} query - Texto de búsqueda
 * @param {number} limit - Cantidad máxima de resultados
 */
const buscarUsuarios = async (query, limit = 10) => {
    try {
        if (!query || query.trim().length < 2) return [];

        const response = await getAllUsers({ search: query.trim(), limit, page: 1 });

        if (!response.data?.users?.length) return [];

        return response.data.users.map(user => ({
            cognitoId: user.cognitoData?.attributes?.sub || user.userId,
            email:     user.email,
            rut:       user.rut,
            nombre:    user.name || user.nombre || user.email.split('@')[0],
            telefono:  user.phone || user.cognitoData?.attributes?.phone_number || '',
        }));
    } catch (error) {
        console.error('Error buscando usuarios:', error);
        return [];
    }
};


export default {
    Create:           Post,
    List:             getAllUsers,
    Update:           updateUser,
    ChangeRol:        updateUserRole,
    Delete:           Delete,
    NextRolSugerido:  nextRolSugerido,
    ToggleStatus:     toggleUserStatus,
    ExportarCSV:      exportarUsuariosCSV,   // 🆕
    ObtenerParaExportar: obtenerUsuariosExport,
    buscarPorEmailORut,
    buscarUsuarios
};
